import { useMemo } from 'react'

import { type ISelectProps, Select, SubText } from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { useFormatTaxPercentage, useProcurifyIntl } from '@webapp/hooks'
import { type Tax } from '@webapp/platform/finance/entities/Tax'
import messages from './messages'
import { useTaxSelect } from './useTaxSelect'

type TaxSelectLikeOption = {
  id: Tax['id'] | string
  name: string
  percentage?: string
  isDefault?: boolean
}

export interface ITaxSelectProps<
  T extends TaxSelectLikeOption = Tax,
> extends ISelectProps {
  defaultTaxId?: number
  /** Prepended to the fetched tax list in render order. */
  extraOptions?: T[]
}

export const TaxSelect = <T extends TaxSelectLikeOption = Tax>({
  defaultTaxId,
  extraOptions,
  ...props
}: ITaxSelectProps<T>) => {
  const intl = useProcurifyIntl()
  const formatTaxPercentage = useFormatTaxPercentage()

  const {
    data: { taxes, defaultValue },
    states: { isFetching },
  } = useTaxSelect({ defaultTaxId })

  const mergedOptions = useMemo(
    () => [...(extraOptions ?? []), ...taxes],
    [extraOptions, taxes]
  )

  const components = useMemo(() => {
    const Option = (optionProps: OptionProps<TaxSelectLikeOption, false>) => {
      const { data, isSelected } = optionProps

      return (
        <ReactSelectComponents.Option {...optionProps}>
          <SubText textEllipsis autoMargin={false}>
            {data.name}
          </SubText>
          {data.percentage !== undefined && (
            <SubText
              color={isSelected ? undefined : 'textSecondary'}
              autoMargin={false}
            >
              {formatTaxPercentage(data.percentage)}
            </SubText>
          )}
          {data.isDefault && (
            <SubText color={isSelected ? undefined : 'textSecondary'}>
              {intl.formatMessage(messages.defaultTaxLabel)}
            </SubText>
          )}
        </ReactSelectComponents.Option>
      )
    }

    const SingleValue = (
      singleValueProps: SingleValueProps<TaxSelectLikeOption, false>
    ) => {
      const { data } = singleValueProps

      return (
        <ReactSelectComponents.SingleValue {...singleValueProps}>
          <SubText autoMargin={false}>{data.name}</SubText>
          {data.percentage !== undefined && (
            <SubText color='textSecondary'>
              {formatTaxPercentage(data.percentage)}
            </SubText>
          )}
        </ReactSelectComponents.SingleValue>
      )
    }

    return { Option, SingleValue }
  }, [formatTaxPercentage, intl])

  return (
    <Select<TaxSelectLikeOption>
      components={components}
      options={mergedOptions}
      isLoading={isFetching}
      getOptionLabel={(opt) => opt.name}
      getOptionValue={(opt) => opt.id?.toString()}
      placeholder={intl.formatMessage(messages.selectPlaceholderText)}
      defaultValue={defaultValue}
      {...props}
    />
  )
}
